import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { opsAPI, opsSubscribe, MODULE_META } from "./opsApi";
import EventCard from "./EventCard";
import { Search, Filter, PauseCircle, PlayCircle } from "lucide-react";

const PRIORITIES = ["", "critical", "high", "normal", "low"];

export default function OpsLiveFeed() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sp] = useSearchParams();
  const [moduleF, setModuleF] = useState(sp.get("module") || "");
  const [priority, setPriority] = useState(sp.get("priority") || "");
  const [paused, setPaused] = useState(false);
  const [live, setLive] = useState(0);
  const bufRef = useRef([]);

  const load = () => opsAPI.feed({
    module:   moduleF || undefined,
    priority: priority || undefined,
    limit:    200,
  }).then(setRows).catch(()=>{});

  useEffect(() => { load(); }, [moduleF, priority]);   // eslint-disable-line

  useEffect(() => {
    const unsub = opsSubscribe(payload => {
      if (payload.type !== "event") return;
      if (moduleF && payload.event.module !== moduleF) return;
      if (priority && payload.event.priority !== priority) return;
      if (paused) { bufRef.current.push(payload.event); setLive(bufRef.current.length); return; }
      setRows(prev => [payload.event, ...prev.filter(r => r.id !== payload.event.id)].slice(0, 300));
    });
    return unsub;
  }, [moduleF, priority, paused]);

  const flush = () => {
    setRows(prev => [...bufRef.current, ...prev].slice(0, 300));
    bufRef.current = []; setLive(0);
  };

  const filtered = useMemo(() =>
    q ? rows.filter(e =>
      (e.title||"").toLowerCase().includes(q.toLowerCase()) ||
      (e.description||"").toLowerCase().includes(q.toLowerCase()) ||
      (e.entity_ref||"").toLowerCase().includes(q.toLowerCase())
    ) : rows
  , [rows, q]);

  return (
    <div className="space-y-4" data-testid="ops-livefeed">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search feed…" aria-label="Search feed"
                 data-testid="ops-feed-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <select value={moduleF} onChange={e=>setModuleF(e.target.value)} data-testid="ops-feed-module"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All modules</option>
          {Object.entries(MODULE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priority} onChange={e=>setPriority(e.target.value)} data-testid="ops-feed-priority"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          {PRIORITIES.map(p => <option key={p} value={p}>{p || "Any priority"}</option>)}
        </select>
        <button onClick={()=>paused ? (setPaused(false), flush()) : setPaused(true)}
                data-testid="ops-feed-pause"
                className={`h-10 px-3 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 border ${
                  paused ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-white text-slate-600 border-slate-200"}`}>
          {paused ? <><PlayCircle className="w-4 h-4"/>Resume{live > 0 && ` (${live} new)`}</> : <><PauseCircle className="w-4 h-4"/>Pause</>}
        </button>
        <div className="ml-auto text-[12px] text-slate-500 mono">{filtered.length} events</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0
          ? <div className="p-12 text-center text-[13px] text-slate-400">No events match this filter.</div>
          : <div className="divide-y divide-slate-100 max-h-[calc(100vh-260px)] overflow-y-auto">
              {filtered.map(e => <EventCard key={e.id} e={e} testid={`ops-feed-${e.id}`}/>)}
            </div>}
      </div>
    </div>
  );
}
